// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import type { CodeSystem, CoverageEligibilityResponse, Patient } from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import type { EligibilityStatus, SchedulingEligibility } from '@polarissleep/scheduling';
import {
  APPOINTMENT_TYPE_SYSTEM,
  determineSchedulingEligibility,
  mapPatientTriageStatusToTriageStage,
  parseAppointmentTypeCodeSystem,
} from '@polarissleep/scheduling';
import { isSelfPayPatient, POLARIS_TRIAGE_STATUS_TAG_SYSTEM } from '@polarissleep/shared';
import { useCallback, useEffect, useState } from 'react';

const eligibilityCache = new Map<string, SchedulingEligibility>();
const eligibilityInFlight = new Map<string, Promise<SchedulingEligibility>>();

/**
 * Hook that checks the current patient's scheduling eligibility.
 * Gathers system status, insurance eligibility, triage stage, and appointment types
 * to determine if the patient can schedule and which types are available.
 * @returns Scheduling eligibility, loading state, error, and refresh function
 */
export function useSchedulingEligibility(): {
  eligibility: SchedulingEligibility | undefined;
  loading: boolean;
  error: Error | null;
  refresh: () => void;
} {
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Patient | undefined;
  const [eligibility, setEligibility] = useState<SchedulingEligibility | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const patientId = profile?.id;

  const fetchEligibility = useCallback(
    async (force = false) => {
      if (!patientId) {
        setEligibility(undefined);
        setLoading(false);
        return;
      }

      if (!force) {
        const cached = eligibilityCache.get(patientId);
        if (cached) {
          setEligibility(cached);
          setLoading(false);
          setError(null);
          return;
        }
      }

      setLoading(true);
      setError(null);

      try {
        let request = eligibilityInFlight.get(patientId);
        if (!request || force) {
          request = (async () => {
            const [coverages, patientResource, codeSystem, eligibilityResponse, bookedAppointments] = await Promise.all(
              [
                medplum.searchResources('Coverage', {
                  beneficiary: `Patient/${patientId}`,
                  status: 'active',
                  _count: '1',
                }),
                medplum.readResource('Patient', patientId),
                medplum.searchOne('CodeSystem', { url: APPOINTMENT_TYPE_SYSTEM }),
                medplum.searchOne('CoverageEligibilityResponse', {
                  patient: `Patient/${patientId}`,
                  _sort: '-created',
                }),
                medplum.searchResources('Appointment', {
                  patient: `Patient/${patientId}`,
                  _count: '100',
                }),
              ]
            );

            let eligibilityStatus: EligibilityStatus;
            const typedResponse = eligibilityResponse as CoverageEligibilityResponse | undefined;
            if (typedResponse?.outcome === 'complete') {
              const inforce = typedResponse.insurance?.[0]?.inforce;
              eligibilityStatus = inforce === true ? 'eligible' : 'ineligible';
            } else if (coverages.length > 0) {
              eligibilityStatus = 'eligible';
            } else {
              eligibilityStatus = 'unknown';
            }

            const triageStatusCode = patientResource.meta?.tag?.find(
              (t) => t.system === POLARIS_TRIAGE_STATUS_TAG_SYSTEM
            )?.code;
            const triageStage = mapPatientTriageStatusToTriageStage(triageStatusCode);
            const appointmentTypes = codeSystem
              ? parseAppointmentTypeCodeSystem((codeSystem as CodeSystem).concept ?? []).filter(
                  (type) => type.coding.code === 'follow-up' && type.schedulable !== false
                )
              : [];

            const now = Date.now();
            const existingBookedTypeCodes = bookedAppointments
              .filter((appt) => {
                const code = appt.appointmentType?.coding?.[0]?.code;
                const isPastBookedFollowUp =
                  appt.status === 'booked' &&
                  code === 'follow-up' &&
                  appt.start !== undefined &&
                  new Date(appt.start).getTime() < now;

                return (
                  !isPastBookedFollowUp &&
                  (appt.status === 'booked' || appt.status === 'pending' || appt.status === 'fulfilled')
                );
              })
              .map((appt) => appt.appointmentType?.coding?.[0]?.code)
              .filter((code): code is string => Boolean(code));

            return determineSchedulingEligibility({
              eligibilityStatus,
              triageStage,
              appointmentTypes,
              existingBookedTypeCodes,
              includeAllTriageStages: true,
              includeNonPortalTypes: true,
              ignoreTriageStage: true,
              isSelfPay: isSelfPayPatient(patientResource as Patient),
            });
          })();
          eligibilityInFlight.set(patientId, request);
        }

        const result = await request;
        eligibilityCache.set(patientId, result);
        setEligibility(result);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to check scheduling eligibility'));
      } finally {
        eligibilityInFlight.delete(patientId);
        setLoading(false);
      }
    },
    [medplum, patientId]
  );

  useEffect(() => {
    fetchEligibility().catch(console.error);
  }, [fetchEligibility]);

  return { eligibility, loading, error, refresh: () => fetchEligibility(true) };
}
